/* eslint-disable import/prefer-default-export */
import { uniq } from "lodash";
import { TInteractivity } from "../types/interactivity";
import {
  EncounterTemplate,
  EncounterTemplateDefinition,
} from "../types/templates";
import { ACL } from "../types/permissions";

const validateTemplateAccess = (
  template: EncounterTemplate,
  key: keyof EncounterTemplateDefinition,
  userRoles: string[],
) => {
  if (!template?.id) return true;

  const requiredKeys = ["hpi", "addendums"];

  const hasKey = template.definition[key] || requiredKeys.includes(key);

  const { acl } = template;

  // no permissions, return true
  if (hasKey && !acl?.length) return true;

  if (hasKey) {
    const allRoles = acl.flatMap((p) => p.roles);

    const hasAllRoles = userRoles.every((role) => allRoles.includes(role));

    // if user has roles not included in all permissions, return true
    if (!hasAllRoles) return true;

    // filter all permissions applied to user roles
    const filteredAcl = acl.filter((p) =>
      p.roles.some((role) => userRoles.includes(role)),
    );

    // allow access if one of user role has access
    return filteredAcl.some(
      (p) =>
        p.permissions["encounter-patient-view"]?.permissions?.[key]?.actions
          ?.view,
    );
  }

  return !!hasKey;
};

const hasACLViewAccess = (
  acl: ACL[],
  key: keyof EncounterTemplateDefinition,
  userRoles: string[],
) => {
  if (!acl.length) return false;
  const applicableACL = acl.filter((p) =>
    p.roles.some((role) => userRoles.includes(role)),
  );
  return applicableACL.some(
    (p) =>
      p.permissions["encounter-patient-view"]?.permissions?.[key]?.actions
        ?.view,
  );
};

// TODO: rename functoin, only applicable to encounters/patient view
export const hasViewAccessByTemplates = (
  templates: EncounterTemplate[] | EncounterTemplate,
  key: keyof EncounterTemplateDefinition,
  userRoles: string[],
) => {
  // handle patient view or past encounter with no template
  if (Array.isArray(templates)) {
    // if no templates, allow view access on all records
    if (!templates?.length) return true;

    // if on patient view or encounters with no templates but no access control on any template, allows view access on all records
    if (templates.every((template) => !template.acl?.length)) {
      return true;
    }

    // if user has roles not included in any acl, allow view access on all records
    const allRoles = uniq(
      templates.flatMap((template) => template.acl?.flatMap((p) => p.roles)),
    );

    if (userRoles.some((role) => !allRoles.includes(role))) {
      return true;
    }

    // true if has view access in any template
    return templates.some((template) =>
      hasACLViewAccess(template.acl, key, userRoles),
    );
  }

  // handle encounter with template

  // if no templates, allow view access on all records
  if (!templates) return true;

  // true if has view access
  return validateTemplateAccess(templates, key, userRoles);
};

const verifyInteractivityHelper = (
  interactivity: TInteractivity,
  template: EncounterTemplate,
  key: keyof EncounterTemplateDefinition,
  userRoles: string[],
) => {
  const { acl } = template;

  // if no permissions, return same interactivity
  if (!acl?.length) return interactivity;

  const allRoles = acl.flatMap((p) => p.roles);
  const hasAllRoles = userRoles.every((role) => allRoles.includes(role));
  // if user has roles not included in all permissions, return true
  if (!hasAllRoles) return interactivity;

  // filter all permissions applied to user roles
  const filteredAcl = acl.filter((p) =>
    p.roles.some((role) => userRoles.includes(role)),
  );

  const hasAccess = filteredAcl.some(
    (p) =>
      p.permissions["encounter-patient-view"]?.permissions?.[key]?.actions
        ?.edit,
  );
  if (hasAccess) return interactivity;

  // if no edit access, return readonly

  return "readonly";
};

// TODO: rename functoin, only applicable to encounters/patient view
export const verifyInteractivity = (
  interactivity: TInteractivity,
  template: EncounterTemplate | EncounterTemplate[],
  key: keyof EncounterTemplateDefinition,
  userRoles: string[],
): TInteractivity => {
  if (interactivity === "readonly") return interactivity;

  // handle patient view or past encounter with no template
  if (Array.isArray(template)) {
    // if no templates or no access control on any template, return same interactivity
    if (!template?.length || template.every((t) => !t.acl?.length))
      return interactivity;

    // eslint-disable-next-line no-restricted-syntax
    for (const t of template) {
      // if has edit access in any template, return same interactivity
      const inter = verifyInteractivityHelper(interactivity, t, key, userRoles);
      if (inter !== "readonly") return inter;
    }

    // if no edit access in no templates, return readonly

    return "readonly";
  }

  // handle encounter with template

  // if no templates, return same interactivity
  if (!template?.id) return interactivity;

  return verifyInteractivityHelper(interactivity, template, key, userRoles);
};
